import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../ui/pagination';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { useIsMobile } from '../ui/use-mobile';
import { Plus, Edit, Trash2, Loader2, AlertTriangle, Filter, X } from 'lucide-react';
import { Student } from '../../types';
import { api } from '../../utils/api';
import { normalizeAcademicYear } from '../../utils/academicYear';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export function StudentManagement() {
  const { accessToken } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const [filterName, setFilterName] = useState<string>('');
  const [filterProgram, setFilterProgram] = useState<string>('all');
  const [filterYearLevel, setFilterYearLevel] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    studentNumber: '',
    name: '',
    email: '',
    department: '',
    course: '',
    curriculumVersion: '',
    yearLevel: 1,
    section: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = accessToken || '';
      console.log('Fetching students with token:', token ? 'Token exists' : 'No token');
      const [studentsData, coursesData] = await Promise.all([
        api.getStudents(token),
        api.getCourses(token)
      ]);
      console.log('Students data received:', studentsData);
      console.log('Number of students:', studentsData.students?.length || 0);
      setStudents(studentsData.students || []);
      setCourses(coursesData.courses || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        toast.error(`Failed to load data: ${error.message}`);
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const token = accessToken || '';
      const payload = {
        ...formData,
        curriculumVersion: normalizeAcademicYear(formData.curriculumVersion) || undefined,
      };
      
      if (editingStudent) {
        await api.updateStudent(editingStudent.id, payload, token);
        toast.success('Student updated successfully');
      } else {
        await api.createStudent(payload, token);
        toast.success('Student created successfully');
      }
      
      await fetchData();
      handleDialogClose();
    } catch (error) {
      console.error('Error saving student:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save student';
      
      // Show helpful message for duplicate email errors
      if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
        toast.error('This email address is already in use. The system will attempt to clean up and retry. Please try again in a moment.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      studentNumber: student.studentNumber,
      name: student.name,
      email: student.email,
      department: student.department || '',
      course: student.course || '',
      curriculumVersion: student.curriculumVersion || '',
      yearLevel: student.yearLevel,
      section: student.section || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (studentId: string) => {
    if (!confirm('Are you sure you want to delete this student?')) {
      return;
    }
    
    try {
      const token = accessToken || '';
      await api.deleteStudent(studentId, token);
      toast.success('Student deleted successfully');
      await fetchData();
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingStudent(null);
    setFormData({
      studentNumber: '',
      name: '',
      email: '',
      department: '',
      course: '',
      curriculumVersion: '',
      yearLevel: 1,
      section: ''
    });
  };

  const getCourseCurriculumVersions = (courseCode: string) => {
    const selectedCourse = courses.find((course) => course.code === courseCode);
    return selectedCourse?.curriculumVersions || [];
  };

  const selectedCourseCurriculumVersions = getCourseCurriculumVersions(formData.course);

  const filteredStudents = students.filter((student) => {
    if (filterName && !student.name.toLowerCase().includes(filterName.toLowerCase())) return false;
    if (filterProgram !== 'all' && student.course !== filterProgram) return false;
    if (filterYearLevel !== 'all' && String(student.yearLevel) !== filterYearLevel) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / rowsPerPage));
  const pageStartIndex = (currentPage - 1) * rowsPerPage;
  const paginatedStudents = filteredStudents.slice(pageStartIndex, pageStartIndex + rowsPerPage);
  const paginationStart = filteredStudents.length === 0 ? 0 : pageStartIndex + 1;
  const paginationEnd = filteredStudents.length === 0
    ? 0
    : Math.min(pageStartIndex + rowsPerPage, filteredStudents.length);

  const hasActiveFilters = filterName !== '' || filterProgram !== 'all' || filterYearLevel !== 'all';

  const clearFilters = () => {
    setFilterName('');
    setFilterProgram('all');
    setFilterYearLevel('all');
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterName, filterProgram, filterYearLevel, rowsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }

    setCurrentPage(page);
  };

  const getVisiblePages = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, currentPage - 1, currentPage, currentPage + 1, totalPages];
  };

  const visiblePages = getVisiblePages();

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
          <div>
            <CardTitle>Student Management</CardTitle>
            <CardDescription className="mt-1">Manage student records and information</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              {/*
              <Button size={isMobile ? "sm" : "default"}>
                <Plus className="mr-2 h-4 w-4" />
                {isMobile ? "Add" : "Add Student"}
              </Button>
              */}
            </DialogTrigger>
            <DialogContent className={isMobile ? "max-w-[95vw] max-h-[90vh] overflow-y-auto" : "max-w-2xl max-h-[90vh] overflow-y-auto"}>
              <DialogHeader>
                <DialogTitle>
                  {editingStudent ? 'Edit Student' : 'Add New Student'}
                </DialogTitle>
                <DialogDescription>
                  {editingStudent 
                    ? 'Update the student information below' 
                    : 'Fill in the details to add a new student.'
                  }
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="studentNumber">Student ID Number</Label>
                  <Input
                    id="studentNumber"
                    value={formData.studentNumber}
                    onChange={(e) => setFormData({...formData, studentNumber: e.target.value})}
                    placeholder="e.g., 2021-001-234"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Juan Dela Cruz"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="e.g., juan.delacruz@nemsu.edu.ph"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Students will use this email address to login.</p>
                </div>
                <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      placeholder="e.g., College of Computing Studies"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="course">Course</Label>
                    <Select value={formData.course} onValueChange={(value) => setFormData({...formData, course: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.code}>
                            {course.code} - {course.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <div className="space-y-2">
                    <Label htmlFor="yearLevel">Year Level</Label>
                    <Select value={formData.yearLevel.toString()} onValueChange={(value) => setFormData({...formData, yearLevel: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1st Year</SelectItem>
                        <SelectItem value="2">2nd Year</SelectItem>
                        <SelectItem value="3">3rd Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="curriculumVersion">Curriculum Year</Label>
                    {selectedCourseCurriculumVersions.length > 0 ? (
                      <Select
                        value={formData.curriculumVersion || 'none'}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          curriculumVersion: value === 'none' ? '' : value,
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select curriculum year" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not assigned</SelectItem>
                          {selectedCourseCurriculumVersions.map((version) => (
                            <SelectItem key={version} value={version}>
                              {version}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="curriculumVersion"
                        value={formData.curriculumVersion}
                        onChange={(e) => setFormData({...formData, curriculumVersion: e.target.value})}
                        placeholder="e.g., 2024-2025"
                      />
                    )}
                    <p className="text-xs text-muted-foreground">
                      Set the curriculum batch this student follows.
                    </p>
                  </div>
                </div>
                <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <div className="space-y-2">
                    <Label htmlFor="section">Section</Label>
                    <Input
                      id="section"
                      value={formData.section}
                      onChange={(e) => setFormData({...formData, section: e.target.value})}
                      placeholder="e.g., A"
                    />
                  </div>
                </div>
                <div className={`flex space-x-2 ${isMobile ? 'flex-col space-y-2 space-x-0' : 'justify-end'}`}>
                  <Button type="button" variant="outline" onClick={handleDialogClose} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingStudent ? 'Update' : 'Add'} Student
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {!loading && students.length > 0 && (
          <div className="mb-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 px-2 text-xs">
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input
                  placeholder="Search by name..."
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Program</Label>
                <Select value={filterProgram} onValueChange={setFilterProgram}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={course.code}>
                        {course.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Year Level</Label>
                <Select value={filterYearLevel} onValueChange={setFilterYearLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Year Levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Year Levels</SelectItem>
                    <SelectItem value="1">1st Year</SelectItem>
                    <SelectItem value="2">2nd Year</SelectItem>
                    <SelectItem value="3">3rd Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : students.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No students found. Add your first student to get started.
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No students match the selected filters.
          </div>
        ) : (
          <>
            {/* Diagnostic Alert for Missing Course Assignments */}
            {students.some(s => !s.course || s.course === '') && (
              <Alert className="mb-4 border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <p className="font-medium text-amber-900 mb-1">⚠️ Action Required: Some students are missing course assignments</p>
                  <p className="text-sm text-amber-800">
                    {students.filter(s => !s.course || s.course === '').length} student(s) don't have a course assigned. 
                    Students without a course won't see any scheduled subjects! Please edit each student and select a course.
                  </p>
                </AlertDescription>
              </Alert>
            )}
            {isMobile ? (
          <div className="space-y-4">
            {paginatedStudents.map((student) => (
              <Card key={student.id} className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">{student.idNumber}</Badge>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(student)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(student.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium">{student.name}</h4>
                    <p className="text-sm text-muted-foreground">{student.email}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{student.course}</span>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {student.curriculumVersion && (
                        <Badge variant="outline" className="text-xs">
                          Curriculum {student.curriculumVersion}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        Year {student.yearLevel} {student.section && `- ${student.section}`}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Curriculum</TableHead>
                  <TableHead>Year Level</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedStudents.map((student) => (
                  <TableRow key={student.id} className={!student.course || student.course === '' ? 'bg-amber-50/50' : ''}>
                    <TableCell>
                      <Badge variant="outline">{student.idNumber}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{student.name}</TableCell>
                    <TableCell>
                      {!student.course || student.course === '' ? (
                        <Badge variant="destructive" className="bg-amber-500">
                          ⚠️ NO COURSE
                        </Badge>
                      ) : (
                        student.course
                      )}
                    </TableCell>
                    <TableCell>
                      {student.curriculumVersion ? (
                        <Badge variant="outline">{student.curriculumVersion}</Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        Year {student.yearLevel} {student.section && `- ${student.section}`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{student.email}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(student)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(student.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
            <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {paginationStart}-{paginationEnd} of {filteredStudents.length} students
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Rows per page</Label>
                  <Select value={String(rowsPerPage)} onValueChange={(value) => setRowsPerPage(Number(value))}>
                    <SelectTrigger className="w-[88px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Pagination className="justify-end sm:justify-center">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    {visiblePages.map((page, index) => {
                      const previousPage = visiblePages[index - 1];
                      const shouldShowEllipsis = previousPage !== undefined && page - previousPage > 1;

                      return (
                        <div key={page} className="flex items-center">
                          {shouldShowEllipsis && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              href="#"
                              isActive={page === currentPage}
                              onClick={(event) => {
                                event.preventDefault();
                                handlePageChange(page);
                              }}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        </div>
                      );
                    })}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}